import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { useCurrentAP } from '../hooks/useCurrentAP';
import { AppLayout, PageContainer } from './layout';
import type { NavItem } from './layout';
import { Spinner } from './ui';
import ProfileSetupNotice from './ap/ProfileSetupNotice';
import { describeAPIdentityState, describeAPShellIdentity } from './ap/apIdentityState';
import { isDebugEnabled } from '../config/debug';

const SchedulePage = lazy(() => import('../pages/ap/SchedulePage'));
const ClientProfilePage = lazy(() => import('../pages/ap/ClientProfilePage'));
const AttendancePage = lazy(() => import('../pages/ap/AttendancePage'));
const ProgressNotesPage = lazy(() => import('../pages/ap/ProgressNotesPage'));
const CompliancePage = lazy(() => import('../pages/ap/CompliancePage'));
const ArchivedAPPage = lazy(() => import('../pages/ap/ArchivedAPPage'));

const apNavItems: NavItem[] = [
  { label: 'Schedule', path: '/ap', icon: 'schedule' },
  { label: 'Attendance', path: '/ap/attendance', icon: 'attendance' },
  { label: 'Notes', path: '/ap/notes', icon: 'notes' },
  { label: 'Compliance', path: '/ap/compliance', icon: 'compliance' },
];

/** Minimal nav for archived APs — only compliance upload access */
const archivedNavItems: NavItem[] = [
  { label: 'Home', path: '/ap', icon: 'home' },
  { label: 'Compliance', path: '/ap/compliance', icon: 'compliance' },
];

/**
 * AP layout shell — wraps all /ap/* routes with responsive navigation.
 *
 * Status-aware routing:
 * - Inactive APs see ArchivedAPPage with compliance upload access only
 * - Active/Onboarding APs see the full AP experience
 * - Unknown users (no AP_Master match) see a setup message
 *
 * The shell names the RESOLVED AP_Master record — `Title · APCode` — and not
 * the Entra guest display name. Those are two different facts, and on
 * 2026-09-02 showing only the second one made a wrong-identity diagnosis look
 * confirmed when it was not (ISS-032): the sidebar read `ZZTest_AP_Andy` while
 * the code had in fact selected `ZA-001`. `describeAPShellIdentity` decides
 * which fact is on screen and captions it, so they can never be conflated
 * again. `AdminShell` is deliberately untouched — an internal user's Entra name
 * IS the right name there.
 */
export default function APShell() {
  const { user, logout } = useAuth();
  const {
    apId,
    apName,
    status,
    isLoading,
    error,
    recordsRead,
    graphReady,
    graphError,
    outcome,
    ambiguous,
    candidates,
  } = useCurrentAP();

  // Which identity the shell shows, and what that identity IS.
  const shellIdentity = describeAPShellIdentity({
    apName,
    apCode: apId,
    signInName: user?.name,
    signInEmail: user?.email,
  });

  const layoutProps = {
    userDisplayName: shellIdentity.displayName,
    userDisplayCaption: shellIdentity.caption,
    onLogout: () => void logout(),
    role: 'ap' as const,
  };

  // While resolving AP profile, show loading
  if (isLoading) {
    return (
      <AppLayout navItems={apNavItems} {...layoutProps}>
        <PageContainer title="">
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem 0' }}>
            <Spinner size="lg" />
          </div>
        </PageContainer>
      </AppLayout>
    );
  }

  // No AP_Master record matched this sign-in, or one matched with no provider
  // code (or the lookup errored). Self-diagnosing notice: it names WHICH of
  // those happened, and shows the exact identity to register.
  if (apId === null) {
    return (
      <AppLayout navItems={apNavItems} {...layoutProps}>
        <ProfileSetupNotice
          email={user?.email ?? null}
          error={error}
          outcome={outcome}
          apCode={apId}
          apName={apName}
          status={status}
          ambiguous={ambiguous}
          candidates={candidates}
          recordsRead={recordsRead}
          identities={user?.identities}
          graphReady={graphReady}
          graphError={graphError}
          onRetry={() => window.location.reload()}
        />
      </AppLayout>
    );
  }

  // Archived (Inactive) AP — supportive experience with compliance access only.
  // Deliberately unchanged: this is the correct design and 35-02 made the
  // SERVER agree with it rather than redesigning the screen.
  if (status === 'Inactive') {
    return (
      <AppLayout navItems={archivedNavItems} {...layoutProps}>
        <Suspense fallback={<PageContainer title=""><Spinner size="lg" /></PageContainer>}>
          <Routes>
            <Route index element={<ArchivedAPPage />} />
            <Route path="compliance" element={<CompliancePage />} />
            <Route path="*" element={<Navigate to="/ap" replace />} />
          </Routes>
        </Suspense>
      </AppLayout>
    );
  }

  // An ambiguity RESOLVES and never blocks: the app below renders exactly as it
  // would otherwise. The notice rides ABOVE it and only under isDebugEnabled(),
  // because a duplicate row is Harmony's problem to fix, not the provider's to
  // read about while their app is working perfectly well.
  const ambiguityState = describeAPIdentityState(outcome, {
    apCode: apId,
    status,
    ambiguous,
    apName,
  });
  const showAmbiguityNotice = ambiguityState.showDebug && isDebugEnabled();

  // Active or Onboarding AP — full experience
  return (
    <AppLayout navItems={apNavItems} {...layoutProps}>
      {showAmbiguityNotice && (
        <ProfileSetupNotice
          email={user?.email ?? null}
          error={null}
          outcome={outcome}
          apCode={apId}
          apName={apName}
          status={status}
          ambiguous={ambiguous}
          candidates={candidates}
          recordsRead={recordsRead}
          identities={user?.identities}
          graphReady={graphReady}
          graphError={graphError}
          compact
        />
      )}
      <Suspense fallback={<PageContainer title=""><Spinner size="lg" /></PageContainer>}>
        <Routes>
          <Route index element={<SchedulePage />} />
          <Route path="clients/:clientId" element={<ClientProfilePage />} />
          <Route path="attendance" element={<AttendancePage />} />
          <Route path="notes" element={<ProgressNotesPage />} />
          <Route path="compliance" element={<CompliancePage />} />
        </Routes>
      </Suspense>
    </AppLayout>
  );
}
